import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { Toggle as BaseToggle } from "@base-ui/react/toggle";
import type { IconData } from "@loot-os/icons/types";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING, PRESS, PRESS_SCALE } from "../foundations/interaction.js";
import { Icon } from "./Icon.js";

/** Selected is `data-pressed` on a toggle chip and `data-checked` on a radio chip. */
const CHIP = cn(
  "inline-flex h-control-sm shrink-0 cursor-pointer items-center gap-1.5 rounded-control border border-border-default bg-bg-surface px-3.5 font-body text-body-b2-strong text-text-secondary",
  "not-data-disabled:hover:border-border-strong not-data-disabled:hover:text-text-primary data-disabled:cursor-default data-disabled:text-text-disabled",
  "data-pressed:border-accent-default data-pressed:bg-accent-subtle data-pressed:text-accent-text",
  "data-checked:border-accent-default data-checked:bg-accent-subtle data-checked:text-accent-text",
  PRESS,
  PRESS_SCALE,
  FOCUS_RING,
);

export interface ChipProps extends Omit<ComponentProps<typeof BaseToggle>, "className"> {
  /** A leading icon, in the chip's text color. */
  icon?: IconData;
  children: ReactNode;
  className?: string;
}

/** A filter that can be switched on and off on its own (`aria-pressed`). */
export function Chip({ icon, className, children, ...props }: ChipProps) {
  return (
    <BaseToggle className={cn(CHIP, className)} {...props}>
      {icon && <Icon icon={icon} className="-ml-0.5 shrink-0" />}
      {children}
    </BaseToggle>
  );
}

export interface ChipOption<Value extends string> {
  value: Value;
  label: ReactNode;
  icon?: IconData;
  /** Shown but not choosable (a view that needs the shopper signed in). */
  disabled?: boolean;
}

export interface ChipRadioGroupProps<Value extends string> {
  options: readonly ChipOption<Value>[];
  value: Value;
  onValueChange: (value: Value) => void;
  /** The group's name, e.g. "Game". */
  label: string;
  className?: string;
}

/**
 * Chips for a filter that always has exactly one value: one Tab stop, arrow keys move and
 * select. Same look as Chip.
 */
export function ChipRadioGroup<Value extends string>({
  options,
  value,
  onValueChange,
  label,
  className,
}: ChipRadioGroupProps<Value>) {
  return (
    <RadioGroup
      aria-label={label}
      value={value}
      onValueChange={(next) => onValueChange(next as Value)}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {options.map((option) => (
        <Radio.Root
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={CHIP}
        >
          {option.icon && <Icon icon={option.icon} className="-ml-0.5 shrink-0" />}
          {option.label}
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
